import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { ACMEListResponse, ACMEResponse } from '@b2b/types/core';

import { AjsResponse, wrapAjs } from '../ajs-response';
import { buildParams, PAGINATION_FIELDS } from '../http-params.util';

/**
 * GiftCardsApiService (Angular 18) — gift card product config (DEV-13071).
 *
 * Contract:
 * - Base path: `/v1/api/giftcards` (dev proxy rewrites to `/v1/giftcards`)
 * - Amounts: bulk-managed only on create(); edit via dedicated `amounts.update()` endpoint
 * - Images: both read (list) and write (create/update/delete) via nested resources
 * - NOT-NULL fields: `active` and `allowCustomAmount` required on create/update
 * - Field naming: `salesChannel` (plural, array) in product VO, not the `saleChannel` query param
 */

const GIFT_CARDS_BASE = '/v1/api/giftcards';

const GIFT_CARDS_SEARCH_FIELDS = [...PAGINATION_FIELDS, 'saleChannel'] as const;

export interface GiftCardProductAmount {
  id?: number | string;
  giftCardProductId?: number | string;
  name: string;
  description?: string;
  amount: number;
}

/** `id` present → update that row; omitted → create a new one. */
export interface GiftCardProductAmountPayload {
  id?: number | string;
  name: string;
  description?: string;
  amount: number;
}

export interface GiftCardProduct {
  id?: number | string;
  active?: boolean;
  allowCustomAmount?: boolean;
  /**
   * "Set Minimum and Maximum Gift Card amounts" (DEV-13995) is not a persisted flag - the
   * backend infers whether a range is configured purely from customAmountMin/Max being
   * present. See GiftCardsComponent, which derives its own local checkbox state the same way.
   */
  customAmountMin?: number | null;
  customAmountMax?: number | null;
  amounts?: GiftCardProductAmount[];
}

export interface GiftCardProductPayload {
  active: boolean;
  allowCustomAmount: boolean;
  customAmountMin?: number | null;
  customAmountMax?: number | null;
  /** Omit entirely to leave existing amounts untouched; send the full desired list otherwise. */
  amounts?: GiftCardProductAmountPayload[];
}

export interface GiftCardProductImage {
  id?: number | string;
  giftCardProductId?: number | string;
  imageId: number | string;
  description?: string;
  primary?: boolean;
}

export interface GiftCardProductImagePayload {
  imageId: number | string;
  description?: string;
  primary?: boolean;
}

@Injectable({ providedIn: 'root' })
export class GiftCardsApiService {
  private readonly http = inject(HttpClient);

  /** GET /v1/api/giftcards — decision 3: the FE always queries the list first to decide create vs. update. */
  list(props?: Record<string, unknown>): Promise<AjsResponse<ACMEListResponse<GiftCardProduct>>> {
    return wrapAjs(
      this.http.get<ACMEListResponse<GiftCardProduct>>(GIFT_CARDS_BASE, {
        params: buildParams(props, GIFT_CARDS_SEARCH_FIELDS),
      }),
    );
  }

  /** POST /v1/api/giftcards — 400 when the tenant already has a product (single-product-per-tenant). */
  create(props: GiftCardProductPayload): Promise<AjsResponse<ACMEResponse<GiftCardProduct>>> {
    return wrapAjs(
      this.http.post<ACMEResponse<GiftCardProduct>>(GIFT_CARDS_BASE, {
        active: props.active,
        allowCustomAmount: props.allowCustomAmount,
        customAmountMin: props.customAmountMin,
        customAmountMax: props.customAmountMax,
        amounts: props.amounts,
      }),
    );
  }

  /** PUT /v1/api/giftcards/{id} — 404 if the id doesn't belong to the tenant. Amounts are omitted (edited via dedicated endpoint instead). */
  update(props: GiftCardProductPayload & { id: number | string }): Promise<AjsResponse<ACMEResponse<GiftCardProduct>>> {
    return wrapAjs(
      this.http.put<ACMEResponse<GiftCardProduct>>(`${GIFT_CARDS_BASE}/${props.id}`, {
        active: props.active,
        allowCustomAmount: props.allowCustomAmount,
        customAmountMin: props.customAmountMin,
        customAmountMax: props.customAmountMax,
      }),
    );
  }

  readonly amounts = {
    /** PUT /v1/api/giftcards/{productId}/amounts/{id} — the only way to edit an existing preset amount; no add/remove (fixed rows). */
    update: (
      productId: number | string,
      props: GiftCardProductAmountPayload & { id: number | string },
    ): Promise<AjsResponse<ACMEResponse<GiftCardProductAmount>>> =>
      wrapAjs(
        this.http.put<ACMEResponse<GiftCardProductAmount>>(`${GIFT_CARDS_BASE}/${productId}/amounts/${props.id}`, {
          name: props.name,
          description: props.description,
          amount: props.amount,
        }),
      ),
  };

  readonly images = {
    /** GET /v1/api/giftcards/{productId}/images */
    list: (productId: number | string): Promise<AjsResponse<ACMEResponse<GiftCardProductImage[]>>> =>
      wrapAjs(this.http.get<ACMEResponse<GiftCardProductImage[]>>(`${GIFT_CARDS_BASE}/${productId}/images`)),

    /** POST /v1/api/giftcards/{productId}/images */
    create: (
      productId: number | string,
      props: GiftCardProductImagePayload,
    ): Promise<AjsResponse<ACMEResponse<GiftCardProductImage>>> =>
      wrapAjs(
        this.http.post<ACMEResponse<GiftCardProductImage>>(`${GIFT_CARDS_BASE}/${productId}/images`, {
          imageId: props.imageId,
          description: props.description,
          primary: props.primary,
        }),
      ),

    /** PUT /v1/api/giftcards/{productId}/images/{id} */
    update: (
      productId: number | string,
      props: GiftCardProductImagePayload & { id: number | string },
    ): Promise<AjsResponse<ACMEResponse<GiftCardProductImage>>> =>
      wrapAjs(
        this.http.put<ACMEResponse<GiftCardProductImage>>(`${GIFT_CARDS_BASE}/${productId}/images/${props.id}`, {
          imageId: props.imageId,
          description: props.description,
          primary: props.primary,
        }),
      ),

    /** DELETE /v1/api/giftcards/{productId}/images/{id} */
    remove: (productId: number | string, id: number | string): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.delete<unknown>(`${GIFT_CARDS_BASE}/${productId}/images/${id}`)),
  };
}
